/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { Spin, Modal, Button, Alert, message } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { saveWithExpiry, getWithExpiry } from '../../../utils/storage';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import { NextDateValidationSchema } from '../../../utils/FormValidation';
import { matchPermissions } from '../../../utils/Permission';
import { SubmitButton } from '../../../components/Button';
import { Breadcrumb } from '../../../components/Card/Cards';
import FeatherIcon from 'feather-icons-react';

// Custom Hooks
import { useAppointmentDetails } from '../../../hooks/useAppointmentDetails';
import { useFormSections } from '../../../hooks/useFormSections';

// New Components
import SectionCard from '../../../components/appointment-details/SectionCard';

// Existing Components
import PatientProfileCard from '../../../components/appointment-details/PatientProfileCard';
import { PatientFormDisplay } from '../../../components/PatientFormDisplay';
import ConsultationReportPDF from '../../../components/appointment-details/ConsultationReportPDF';
import DocumentConsultationTables from '../../../components/appointment-details/DocumentConsultationTables';
import ExaminationSection from '../../../components/appointment-details/ExaminationSection';
import LaboratoryInvestigations from '../../../components/appointment-details/LaboratoryInvestigations';
import DiagnosisSection from '../../../components/appointment-details/DiagnosisSection';
import TreatmentRecommendation from '../../../components/appointment-details/TreatmentRecommendation';
import PrescriptionTable from '../../../components/appointment-details/PrescriptionTable';
import DoDontSection from '../../../components/appointment-details/DoDontSection';
import NotesSection from '../../../components/appointment-details/NotesSection';
import NextConsultationSection from '../../../components/appointment-details/NextConsultationSection';
import VirechanExerciseSection from '../../../components/appointment-details/VirechanExerciseSection';
import { useEffect } from 'react';

/**
 * AppointmentDetails Page - Revamped Doctor-Friendly UI
 * Features:
 * - Visual progress tracking
 * - Collapsible sections with completion indicators
 * - Clear workflow for assistant and main doctor
 * - Minimal clicks with smart defaults
 * - Mobile and tablet optimized
 */
const AppointmentDetails = () => {
  // Use custom hook for all state and logic
  const {
    ParamPage,
    status,
    notes,
    setNotes,
    isEditingChiefComplaint,
    setIsEditingChiefComplaint,
    tempChiefComplaint,
    setTempChiefComplaint,
    showStatusModal,
    setShowStatusModal,
    pendingSubmission,
    medicineList,
    loader,
    MedicalDetail,
    CcPaperDetail,
    ConsultationMasterData,
    AppointmentData,
    userDetails,
    medicalDraft,
    medicalDraftLoader,
    IsFilledForm,
    patientInfo,
    initialValues,
    handleFormSubmit,
    handleStatusSelection,
    handleCancelStatusModal,
    handleUpdatePatientForm,
    ParamAppointmentId,
  } = useAppointmentDetails();

  const matchPermissionsForUser = matchPermissions(userDetails);
  const localDraftKey = `patient_draft_${ParamAppointmentId}`;

  // Log draft data when it's loaded (for debugging)
  useEffect(() => {
    if (medicalDraft && medicalDraft.length > 0) {
console.log('🔄 Draft Loader Status:', medicalDraftLoader);
}

    // Check for local browser draft
    const localDraft = getWithExpiry(localDraftKey);
    if (localDraft && !IsFilledForm) {
      message.info({
        content: 'Found a local draft saved in this browser. You can use it if server draft is missing.',
        duration: 5,
      });
    }
  }, [medicalDraft, medicalDraftLoader, ParamAppointmentId]);

  // Auto-save component to be used inside Formik
  const AutoSave = () => {
    const { values, dirty } = useFormikContext();

    useEffect(() => {
      if (dirty && !IsFilledForm) {
        saveWithExpiry(localDraftKey, values, 48); // 48 hours = 2 days
      }
    }, [values, dirty]);

    return null;
  };

  return (
    <div className="appointment-details-page">
      <Header />
      <Sidebar id="menu-item1" id1="menu-items1" activeClassName="add-staff" />

      <div className={`page-wrapper ${loader ? 'loader-active' : ''}`}>
        {/* Loading Overlay */}
        {loader && <div className="loader-overlay"></div>}
        {loader && (
          <div className="loader-container">
            <Spin size="large" />
          </div>
        )}

        <div className="content">
          {/* Breadcrumb Navigation */}
          <Breadcrumb
            page={[
              { name: 'Patient', linkTo: `/staff-list?page=${ParamPage}` },
              { name: 'Patient List', linkTo: `/staff-list?page=${ParamPage}` },
              { name: 'Patient Profile', linkTo: `/staff-list?page=${ParamPage}` },
              { name: 'Appointment Detail' },
            ]}
          />
          {/* Patient Profile Card */}
          <PatientProfileCard
            PatientName={patientInfo.name}
            PatientPhoneNo={patientInfo.phoneNo}
            PatientGender={patientInfo.gender}
            PatientBirthDate={patientInfo.birthDate}
            PatientRegistrationNo={patientInfo.registrationNo}
            PatientDateOfConsultation={patientInfo.dateOfConsultation}
            PatientConsultatingDoctor={patientInfo.consultatingDoctor}
            PatientID={patientInfo.id}
            treatmentDetails={patientInfo.treatmentDetails}
            CcPaperDetail={CcPaperDetail}
            isEditingChiefComplaint={isEditingChiefComplaint}
            tempChiefComplaint={tempChiefComplaint}
            setIsEditingChiefComplaint={setIsEditingChiefComplaint}
            setTempChiefComplaint={setTempChiefComplaint}
            IsFilledForm={IsFilledForm}
            MedicalDetail={MedicalDetail}
          />
          {/* Investigation Reports Section */}
          <SectionCard
            title="Investigation Reports & Summary"
            icon="file-text"
            defaultOpen={true}
            isRequired={false}
          >
            <div className="form-group">
              <textarea
                className="form-control"
                rows={10}
                placeholder="Add investigation reports and summary here..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                disabled={IsFilledForm}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  padding: '12px',
                  fontSize: '14px',
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                💡 Include any relevant lab reports, imaging results, or clinical observations
              </p>
            </div>
          </SectionCard>

          {/* Patient Form Display (Editable) */}
          <PatientFormDisplay
            patientData={CcPaperDetail}
            viewOnly={false}
            onSubmit={handleUpdatePatientForm}
          />

          {/* Draft Data Indicator */}
          {!IsFilledForm && ((medicalDraft && medicalDraft.length > 0) || getWithExpiry(localDraftKey)) && !medicalDraftLoader && (
            <div style={{ padding: '24px' }}>
              <Alert
                message="Draft Data Found"
                description={getWithExpiry(localDraftKey) ? "A local draft or server draft has been found. This helps prevent data loss if you refresh the page." : "A saved draft has been found for this appointment and has been pre-filled into the form below."}
                type="info"
                showIcon
                icon={<FeatherIcon icon="file-text" size={16} />}
                closable
                style={{
                  borderRadius: '8px',
                  border: '1px solid #91d5ff',
                  background: '#e6f7ff',
                }}
              />
            </div>
          )}
        </div>

        {/* Main Form */}
        <Formik
          enableReinitialize={true}
          initialValues={getWithExpiry(localDraftKey) || initialValues}
          validationSchema={NextDateValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit,
            setValues,
            isSubmitting,
          }) => {
            // Use form sections hook
            const { getSectionsWithStatus, getFormCompletion } = useFormSections();
            const sectionsWithStatus = getSectionsWithStatus(values);
            const formCompletion = getFormCompletion(values);

            return (
              <>
                <AutoSave />


                {/* Consultation Report PDF Component */}
                <ConsultationReportPDF
                  formikValues={values}
                  MedicalDetail={MedicalDetail}
                  CcPaperDetail={CcPaperDetail}
                  AppointmentData={AppointmentData}
                  PatientName={patientInfo.name}
                  PatientPhoneNo={patientInfo.phoneNo}
                  PatientGender={patientInfo.gender}
                  PatientBirthDate={patientInfo.birthDate}
                  PatientRegistrationNo={patientInfo.registrationNo}
                  PatientDateOfConsultation={patientInfo.dateOfConsultation}
                  PatientConsultatingDoctor={patientInfo.consultatingDoctor}
                  tempChiefComplaint={tempChiefComplaint}
                  treatmentDetails={patientInfo.treatmentDetails}
                />

                <div className="mx-4">

                  {/* Form Validation Alert */}
                  {/* {!IsFilledForm && !formCompletion.isFormValid && (
                    <Alert
                      message="Required Sections Incomplete"
                      description={`Please complete ${formCompletion.totalRequired - formCompletion.completedRequired} required section(s) before submitting.`}
                      type="warning"
                      showIcon
                      icon={<FeatherIcon icon="alert-circle" size="16" />}
                      style={{ marginBottom: '20px', borderRadius: '8px' }}
                    />
                  )} */}


                  <fieldset disabled={IsFilledForm} style={{ border: 'none', padding: 0 }}>
                    {/* Document and Consultation Tables */}
                    <SectionCard
                      title="Previous Consultations"
                      icon="clock"
                      defaultOpen={false}
                      isRequired={false}
                    >
                      <DocumentConsultationTables CcPaperDetail={CcPaperDetail} />
                    </SectionCard>

                    {/* Examination Section */}
                    <SectionCard
                      title="Physical Examination"
                      icon="clipboard"
                      defaultOpen={true}
                      isRequired={true}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'examination')?.isCompleted}
                    >
                      <ExaminationSection
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        IsFilledForm={IsFilledForm}
                      />
                    </SectionCard>
                  </fieldset>

                  {/* Laboratory Investigations */}
                  <SectionCard
                    title="Laboratory Investigations"
                    icon="activity"
                    defaultOpen={true}
                    isRequired={false}
                    isCompleted={sectionsWithStatus.find(s => s.id === 'laboratory')?.isCompleted}
                  >
                    <LaboratoryInvestigations
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setValues={setValues}
                      IsFilledForm={IsFilledForm}
                      ConsultationMasterData={ConsultationMasterData}
                      notes={notes}
                      setNotes={setNotes}
                    />
                  </SectionCard>

                  <fieldset disabled={IsFilledForm} style={{ border: 'none', padding: 0 }}>
                    {/* Diagnosis Section */}
                    <SectionCard
                      title="Diagnosis & Root Cause"
                      icon="file-text"
                      defaultOpen={true}
                      isRequired={true}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'diagnosis')?.isCompleted}
                    >
                      <DiagnosisSection
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        IsFilledForm={IsFilledForm}
                      />
                    </SectionCard>

                    {/* Treatment and Solution Recommendation */}
                    <SectionCard
                      title="Treatment Plan & Recommendations"
                      icon="heart"
                      defaultOpen={true}
                      isRequired={true}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'treatment')?.isCompleted}
                    >
                      <TreatmentRecommendation
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        IsFilledForm={IsFilledForm}
                        ConsultationMasterData={ConsultationMasterData}
                      />
                    </SectionCard>

                    {/* Prescription Table */}
                    <SectionCard
                      title="Prescription"
                      icon="file-plus"
                      badge={values?.recomendedMedicines?.filter(m => m.medicineName).length || 0}
                      badgeColor="#8b5cf6"
                      defaultOpen={true}
                      isRequired={true}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'prescription')?.isCompleted}
                    >
                      <PrescriptionTable
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        IsFilledForm={IsFilledForm}
                        medicineList={medicineList}
                        loader={loader}
                      />
                    </SectionCard>

                    {/* Do and Don'ts Section */}
                    <SectionCard
                      title="Lifestyle Advice (Do's & Don'ts)"
                      icon="sun"
                      defaultOpen={true}
                      isRequired={false}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'lifestyle')?.isCompleted}
                    >
                      <DoDontSection
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        IsFilledForm={IsFilledForm}
                        ConsultationMasterData={ConsultationMasterData}
                      />
                      <VirechanExerciseSection
                        values={values}
                        handleChange={handleChange}
                        IsFilledForm={IsFilledForm}
                        ConsultationMasterData={ConsultationMasterData}
                      />
                    </SectionCard>

                    {/* Notes Section */}
                    <SectionCard
                      title="Doctor's Notes"
                      icon="edit-3"
                      defaultOpen={true}
                      isRequired={false}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'notes')?.isCompleted}
                    >
                      <NotesSection
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        IsFilledForm={IsFilledForm}
                      />
                    </SectionCard>

                    {/* Next Consultation Date */}
                    <SectionCard
                      title="Follow-up Consultation"
                      icon="calendar"
                      defaultOpen={true}
                      isRequired={true}
                      isCompleted={sectionsWithStatus.find(s => s.id === 'followup')?.isCompleted}
                    >
                      <NextConsultationSection
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        IsFilledForm={IsFilledForm}
                      />
                    </SectionCard>

                    {/* Submit Button */}
                    {matchPermissionsForUser('medical.create') && !IsFilledForm && (
                      <div
                        style={{
                          position: 'sticky',
                          bottom: '20px',
                          zIndex: 100,
                          marginTop: '24px',
                        }}
                      >
                        <div
                          style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            padding: '20px',
                            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '16px',
                          }}
                        >
                          <SubmitButton
                            type="primary"
                            size="large"
                            loading={isSubmitting}
                            onClick={() => handleSubmit()}
                            disabled={IsFilledForm}
                            style={{
                              height: '48px',
                              padding: '0 32px',
                              fontSize: '16px',
                              fontWeight: '600',
                              borderRadius: '8px',
                              background: '#4f46e5',
                              border: 'none',
                            }}
                          >
                            <FeatherIcon icon="check-circle" size="18" style={{ marginRight: '8px' }} />
                            Draft / Submit Consultation
                          </SubmitButton>
                        </div>
                      </div>
                    )}
                  </fieldset>
                </div>
              </>
            );
          }}
        </Formik>

        {/* Status Selection Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FeatherIcon icon="check-circle" size="20" style={{ color: '#4f46e5' }} />
              <span>Appointment Status</span>
            </div>
          }
          open={showStatusModal}
          onCancel={() => handleCancelStatusModal()}
          footer={null}
          centered
          width={500}
        >
          <div style={{ padding: '20px 0' }}>
            <p style={{ fontSize: '15px', marginBottom: '24px', color: '#4b5563' }}>
              Please select the status for this appointment:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                type="primary"
                size="large"
                icon={<FeatherIcon icon="check-circle" size="18" />}
                onClick={() => handleStatusSelection('Completed')}
                disabled={!pendingSubmission?.values?.nextConsultationDate}
                style={{
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  backgroundColor: !pendingSubmission?.values?.nextConsultationDate ? '#d1d5db' : '#10b981',
                  borderColor: !pendingSubmission?.values?.nextConsultationDate ? '#d1d5db' : '#10b981',
                  cursor: !pendingSubmission?.values?.nextConsultationDate ? 'not-allowed' : 'pointer',
                }}
              >
                Complete Appointment
              </Button>
              {!pendingSubmission?.values?.nextConsultationDate && (
                <p style={{ fontSize: '11px', color: '#ef4444', marginBottom: '4px', textAlign: 'center' }}>
                  * Please set a Next Consultation Date to complete the appointment
                </p>
              )}
              <Button
                size="large"
                icon={<FeatherIcon icon="save" size="18" />}
                onClick={() => handleStatusSelection('Draft')}
                style={{
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                Save as Draft
              </Button>
            </div>
            <p style={{ fontSize: '12px', marginTop: '16px', color: '#9ca3af', marginBottom: 0 }}>
              💡 <strong>Tip:</strong> Choose &ldquo;Complete&rdquo; if the consultation is finished, or &ldquo;Draft&rdquo; to continue later.
            </p>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AppointmentDetails;